import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, User, Mail, Phone, FileText, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import { formatDate } from '../utils/Config';
import { fetchJobApplications, updateJobApplication } from '../redux/JobApplicationsSlice';

const APPLICATION_STATUSES = ['All Statuses', 'pending', 'shortlisted', 'rejected', 'hired'];

const ApplicationFilters = ({ filters, onFilterChange }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <select
                value={filters.status || 'All Statuses'}
                onChange={(e) => onFilterChange('status', e.target.value)}
                className="w-fit px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                {APPLICATION_STATUSES.map(status => (
                    <option key={status} value={status}>{status}</option>
                ))}
            </select>
        </div>
    );
};

const ApplicationTable = ({ applications }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleView = (id) => {
        navigate(`/dashboard/job-applications/${id}`);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'shortlisted':
                return 'bg-blue-100 text-blue-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            case 'hired':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };


    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Applicant
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Job Details
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Applied Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {applications?.map((application) => (
                        <tr key={application.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                            <User className="h-6 w-6 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {`${application.user.first_name} ${application.user.last_name}`}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {application.user.email}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm font-medium text-gray-900">
                                    {application.job_posting.title}
                                </div>
                                <div className="text-sm text-gray-500">
                                    {application.job_posting.company_name}
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center text-sm text-gray-500">
                                        <Mail className="h-4 w-4 mr-2" />
                                        {application.email}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500">
                                        <Phone className="h-4 w-4 mr-2" />
                                        {application.phone}
                                    </div>
                                </div>
                            </td>
                            <td className={`px-6 py-4`}>
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                                    {application.status}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center text-sm text-gray-500">
                                    <Clock className="h-4 w-4 mr-2" />
                                    {formatDate(application.created_at)}
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex gap-2 items-center">
                                    <button
                                        onClick={() => handleView(application.id)}
                                        className="text-blue-600 hover:text-blue-900"
                                        title="View Details"
                                    >
                                        <Eye className="h-5 w-5" />
                                    </button>
                                    {application.resume && (
                                        <a
                                            href={application.resume}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-600 hover:text-gray-900"
                                            title="View Resume"
                                        >
                                            <FileText className="h-5 w-5" />
                                        </a>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const JobApplications = () => {
    const dispatch = useDispatch();
    const applications = useSelector(state => state.jobApplications.items);
    const { loading, error } = useSelector((state) => state.jobApplications);
    const navigate = useNavigate()

    const [filters, setFilters] = useState({
        status: null
    });

    const {
        pagination,
        handlePageChange,
        updatePaginationData,
        getPageNumbers,
        getItemRange
    } = usePagination({
        initialPage: 1,
        initialPerPage: 15
    });

    useEffect(() => {
        handleFetchApplications();
    }, [pagination.currentPage, filters]);

    const handleFetchApplications = () => {
        const queryParams = {
            page: pagination.currentPage,
            limit: pagination.perPage
        };

        if (filters.status && filters.status !== 'All Statuses') {
            queryParams['filter[status]'] = filters.status;
        }
        console.log(queryParams)
        dispatch(fetchJobApplications(queryParams))
            .unwrap()
            .then(response => {
                if (response.meta) {
                    updatePaginationData({
                        current_page: response.meta.current_page,
                        last_page: response.meta.last_page,
                        per_page: response.meta.per_page,
                        total: response.meta.total
                    });
                }
            })
            .catch((error) => {
                toast.error(error.message || 'Failed to fetch applications');
            });
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value === `All ${filterType}s` ? null : value
        }));
        handlePageChange(1);
    };

    return (
        <div className="p-4">
            <button
                onClick={() => navigate('/dashboard/jobs')}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
            >
                <ArrowLeft size={20} className="mr-2" />
                Back
            </button>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Job Applications</h1>

            </div>

            <ApplicationFilters
                filters={filters}
                onFilterChange={handleFilterChange}
            />

            {loading ? (
                <div className="flex justify-center items-center h-[80vh]">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                </div>
            ) : (
                <div className="my-6">
                    <ApplicationTable applications={applications} />
                </div>
            )}

            {!loading && applications.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                    No applications found
                </div>
            )}

            <Pagination
                currentPage={pagination.currentPage}
                lastPage={pagination.lastPage}
                total={pagination.total}
                itemRange={getItemRange()}
                pageNumbers={getPageNumbers()}
                onPageChange={handlePageChange}
            />
        </div>
    );
};

export default JobApplications;